import React from 'react';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Paper } from '@lifeomic/chroma-react/components/Paper';
import { useLoadAuditEvents } from './useLoadAuditEvents';
import { useLoadHistogramData } from './useLoadHistogramData';
import AuditEventTable from './AuditEventTable';
import AuditEventCountPill from './AuditEventCountPill';
import AuditSearchField from './AuditSearchField';
import AuditEventModal from './AuditEventModal';
import Histogram from './Histogram';
import TimeFilterChip from './TimeFilterChip';

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  tableHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    width: '100%',
  },
  tableContainer: {
    maxHeight: theme.pxToRem(400),
    overflowY: 'auto',
  },
}));

const AuditEvents = () => {
  const classes = useStyles({});

  const [focusedEvent, setFocusedEvent] =
    React.useState<fhir.AuditEvent | null>(null);
  const [searchValue, setSearchValue] = React.useState('*');
  const [filterTimes, setFilterTimes] = React.useState({
    maxTime: Date.now() + 1000,
    minTime: 0,
  });

  const { events, isLoading, hasMoreToLoad, loadMore, count } =
    useLoadAuditEvents(searchValue, filterTimes);
  const { histogramData, maxTime, error } = useLoadHistogramData(
    searchValue,
    filterTimes,
  );

  return (
    <Paper padding={0} className={classes.root}>
      <Box className={classes.tableHeader}>
        <AuditSearchField setSearchValue={setSearchValue} />
        <TimeFilterChip
          filterTimes={filterTimes}
          setFilterTimes={setFilterTimes}
        />
        <AuditEventCountPill count={count} />
      </Box>
      {!error && (
        <Histogram
          data={histogramData}
          maxTime={maxTime}
          setFilterTimes={setFilterTimes}
        />
      )}
      <div className={classes.tableContainer}>
        <AuditEventTable
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          events={events}
          setFocusedEvent={setFocusedEvent}
          isLoading={isLoading}
          hasMoreToLoad={hasMoreToLoad}
          loadMore={loadMore}
        />
      </div>
      <AuditEventModal
        focusedEvent={focusedEvent}
        setFocusedEvent={setFocusedEvent}
      />
    </Paper>
  );
};

export default React.memo(AuditEvents);
